import { useEffect, useState } from 'react';
import { FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const Sparkle = ({
  className,
  size = 8,
}: {
  className?: string;
  size?: number;
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
  >
    <path d="M12 0L14.59 8.41L23 12L14.59 15.59L12 24L9.41 15.59L1 12L9.41 8.41L12 0Z" />
  </svg>
);

export const SignalAILoadingState = () => {
  const { t } = useTranslation('knowledge');
  const [phase, setPhase] = useState<'analyzing' | 'extracting'>('analyzing');

  useEffect(() => {
    const timer = setTimeout(() => setPhase('extracting'), 3000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <style>{`
        @keyframes float-up-1 {
          0% { transform: translateY(0) scale(0); opacity: 0; }
          20% { transform: translateY(-8px) scale(1); opacity: 1; }
          80% { transform: translateY(-40px) scale(0.8); opacity: 0.6; }
          100% { transform: translateY(-56px) scale(0); opacity: 0; }
        }
        @keyframes float-up-2 {
          0% { transform: translateY(0) scale(0) rotate(0deg); opacity: 0; }
          25% { transform: translateY(-12px) scale(1) rotate(45deg); opacity: 1; }
          75% { transform: translateY(-48px) scale(0.6) rotate(90deg); opacity: 0.5; }
          100% { transform: translateY(-64px) scale(0) rotate(135deg); opacity: 0; }
        }
        @keyframes float-up-3 {
          0% { transform: translateY(0) scale(0); opacity: 0; }
          15% { transform: translateY(-6px) scale(1); opacity: 0.9; }
          85% { transform: translateY(-52px) scale(0.7); opacity: 0.4; }
          100% { transform: translateY(-60px) scale(0); opacity: 0; }
        }
        @keyframes gentle-bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        @keyframes glow-pulse {
          0%, 100% { opacity: 0.15; transform: scale(1); }
          50% { opacity: 0.3; transform: scale(1.1); }
        }
        @keyframes orbit {
          0% { transform: rotate(0deg) translateX(52px) rotate(0deg) scale(0.8); opacity: 0.7; }
          50% { transform: rotate(180deg) translateX(52px) rotate(-180deg) scale(1); opacity: 1; }
          100% { transform: rotate(360deg) translateX(52px) rotate(-360deg) scale(0.8); opacity: 0.7; }
        }
        .sparkle-float-1 { animation: float-up-1 2.4s ease-out infinite; }
        .sparkle-float-2 { animation: float-up-2 2.8s ease-out infinite 0.6s; }
        .sparkle-float-3 { animation: float-up-3 3.2s ease-out infinite 1.2s; }
        .sparkle-float-4 { animation: float-up-1 2.6s ease-out infinite 0.9s; }
        .sparkle-float-5 { animation: float-up-2 3s ease-out infinite 1.8s; }
        .icon-bounce { animation: gentle-bounce 2.5s ease-in-out infinite; }
        .glow-pulse { animation: glow-pulse 2.5s ease-in-out infinite; }
        .orbit-sparkle { animation: orbit 4s linear infinite; }
        .orbit-sparkle-2 { animation: orbit 5s linear infinite reverse; }
        .orbit-sparkle-3 { animation: orbit 3.5s linear infinite 1s; }
        @keyframes opacity-pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        .blur-text-in { animation: opacity-pulse 2s ease-in-out infinite; }
      `}</style>

      <div className="flex flex-col items-center justify-center py-16">
        {/* Icon area with glow and particles */}
        <div className="relative mb-8">
          {/* Background glow */}
          <div className="glow-pulse absolute inset-0 -m-8 rounded-full bg-primary/20 blur-2xl" />

          {/* Orbiting sparkles */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="orbit-sparkle absolute">
              <Sparkle size={10} className="text-primary/70" />
            </div>
            <div className="orbit-sparkle-2 absolute">
              <Sparkle size={7} className="text-blue-400/60" />
            </div>
            <div className="orbit-sparkle-3 absolute">
              <Sparkle size={8} className="text-indigo-400/50" />
            </div>
          </div>

          {/* Floating sparkles rising up */}
          <div className="absolute -top-2 start-1/2 -translate-x-1/2">
            <div className="relative h-16 w-24">
              <Sparkle
                size={8}
                className="sparkle-float-1 absolute bottom-0 start-3 text-primary"
              />
              <Sparkle
                size={6}
                className="sparkle-float-2 absolute bottom-0 start-10 text-blue-400"
              />
              <Sparkle
                size={10}
                className="sparkle-float-3 absolute bottom-0 start-6 text-indigo-400"
              />
              <Sparkle
                size={5}
                className="sparkle-float-4 absolute bottom-0 start-14 text-primary/80"
              />
              <Sparkle
                size={7}
                className="sparkle-float-5 absolute bottom-0 start-8 text-blue-300"
              />
            </div>
          </div>

          {/* Main icon */}
          <div className="icon-bounce relative z-10 flex h-20 w-20 items-center justify-center rounded-2xl border border-primary/20 bg-white shadow-lg shadow-primary/10">
            <FileText className="h-10 w-10 text-primary" />
          </div>
        </div>

        {/* Text */}
        <div className="text-center">
          <p
            key={phase}
            className="blur-text-in text-base text-muted-foreground"
          >
            {phase === 'analyzing'
              ? t('aiAnalyzingDocuments')
              : t('aiExtractingSignals')}
          </p>
        </div>
      </div>
    </>
  );
};
